import * as React from "react";
import { Button } from "@/components/ui/button";
import type { PipelineRunResponse } from "@/types/contract-family-pipeline";
import { FailedSnapshotsDialog } from "./FailedSnapshotsDialog";

export const SnapshotCreationStage = ({
  run,
  isLoading,
  onRetry,
  onExclude,
}: {
  run: PipelineRunResponse;
  isLoading: boolean;
  onRetry: () => void;
  onExclude: (documentIds: string[]) => void;
}) => {
  const [open, setOpen] = React.useState(false);
  const counts = run.snapshot_counts;
  const progress = counts && counts.total > 0 ? ((counts.completed + counts.failed) / counts.total) * 100 : 0;
  const isProcessing = Boolean(counts?.pending);
  return (
    <div className="space-y-3">
      <div role="status" aria-live="polite" className="flex items-start gap-3">
        {isProcessing ? (
          <span
            aria-hidden="true"
            className="mt-0.5 h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-desert-sand-200 border-t-desert-sand-700 motion-reduce:animate-none"
          />
        ) : null}
        <div className="min-w-0 flex-1">
          <p className="font-medium">Creating document snapshots</p>
          {counts && (
            <>
              <p className="text-sm text-slate-600">
                {counts.completed} completed, {counts.failed} failed, {counts.total} total
              </p>
              <div
                role="progressbar"
                aria-label="Snapshot progress"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={progress}
                className="relative mt-2 h-2 overflow-hidden rounded bg-slate-100"
              >
                <div className="h-full bg-desert-sand-500 transition-[width]" style={{ width: `${progress}%` }} />
                {isProcessing ? (
                  <div className="absolute inset-y-0 w-1/4 animate-pulse bg-white/40 motion-reduce:animate-none" />
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>
      {counts?.failed ? (
        <div className="flex gap-2">
          <Button variant="outline" onClick={onRetry} disabled={isLoading}>
            Retry failed
          </Button>
          <Button variant="outline" onClick={() => setOpen(true)} disabled={isLoading}>
            Continue without failed documents
          </Button>
        </div>
      ) : null}
      <FailedSnapshotsDialog
        open={open}
        onOpenChange={setOpen}
        documents={run.failed_documents}
        isLoading={isLoading}
        onConfirm={() => onExclude(run.failed_documents.map(document => document.document_id))}
      />
    </div>
  );
};
